import {useTheme} from "@mui/material/styles";
import {useQueryClient} from "@tanstack/react-query";
import {useApp} from "../../../utils/global-contexts/AppContext";
import React, {useEffect, useMemo, useState} from "react";
import {MaterialReactTable, MRT_ActionMenuItem, MRT_ColumnDef, useMaterialReactTable} from "material-react-table";
import {
    getMuiTableBodyCellProps,
    getMuiTableBodyRowProps,
    getMuiTableHeadCellProps,
    getMuiTablePaperProps
} from "../../../ui/MuiReactTable/MuiReactTableStyles";
import {Avatar, Box, Grid} from "@mui/material";
import MuiReadOnlyBoxBody from "../../../ui/MuiDisplayData/MuiReadOnlyBoxBody";
import Images from "../../../assets/images";
import {Add, Delete, Edit} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import useCustomCellRenderer from "./useCustomCellRenderer";
import {ICategory} from "../../../utils/interfaces/Catalogue/ICategory";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import UndoButton from "../../../ui/Buttons/UndoButton";
import ProcessingTable from "../Processing/ProcessingTable";
import MuiModal from "../../../ui/MuiModals/MuiModal";
import useApiGetCatalogue from "../api-hooks/useApiGetCatalogue";
import CategoryForm from "./CategoryForm";
import useApiDeleteCategory from "./api-hooks/useApiDeleteCategory";
import MuiAlertDialog from "../../../ui/MuiAlertDialog";
import MRTMuiButtonWithAvatar from "../../../ui/MuiReactTable/MRTMuiButtonWithAvatar";
import {MRT_Localization_IT} from "material-react-table/locales/it";


interface Props {
    alreadySelectedObj?: ICategory | undefined,
    onSelect?: (obj: ICategory | undefined) => void,
    onClose?: () => void;
}


const CategoryTable = ({onSelect, onClose, alreadySelectedObj}: Props) => {
    const theme = useTheme();
    const {t} = useTranslation()
    const {setSelectedCategory} = useApp();
    const queryClient = useQueryClient();

    const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const deleteMutation = useApiDeleteCategory();
    const {mutateAsync: apiDelete} = deleteMutation;
    const handleConfirmDelete = async () => {
        try {
            await apiDelete({pk: selectedRow?.id});
        } catch (error) {
        }
        setDeleteConfirmationOpen(false);
    };
    const handleCancelDelete = () => {
        setDeleteConfirmationOpen(false);
    };

    const [selectedRow, setSelectedRow] = useState<ICategory | undefined>()
    const [rows, setRows] = useState<ICategory[] | undefined>()

    const [isEditFormOpen, setIsEditFormOpen] = useState<boolean>(false)
    const [isProcessingTableOpen, setIsProcessingTableOpen] = useState<boolean>(false)

    const {data: fullCatalogue} = useApiGetCatalogue();

    useEffect(() => {
        if (fullCatalogue) {
            setRows(fullCatalogue.categories);
        }
    }, [fullCatalogue]);


    const CustomCellRenderer: React.FC<{ row: ICategory, theme: any }> = ({row, theme}) => {
        const {renderIcons} = useCustomCellRenderer(row, theme);
        return renderIcons();
    };


    const columns = useMemo<MRT_ColumnDef<ICategory>[]>(
        () => [
            {
                id: "custom_actions",
                header: t("processings"),
                columnDefType: "display",
                Cell: ({row}) => (
                    <Box sx={{display: 'flex'}}>
                        <Avatar sx={{
                            backgroundColor: theme.palette.greenSoft.main,
                            border: `2px solid ${theme.palette.primary.main}`,
                            marginRight: 1,
                        }}>
                            <img src={Images.navbar_lavorazioni_yellow} alt={'processings'}
                                 style={{width: '24px', height: '24px', cursor: 'pointer'}}
                                 onClick={
                                     () => {
                                         setSelectedCategory(row.original);
                                         setSelectedRow(row.original);
                                         setIsProcessingTableOpen(true);
                                     }
                                 }
                            />
                        </Avatar>
                        <span>{row.original.processings.length}</span>
                    </Box>
                )
            },
            {
                accessorKey: 'label',
                header: t('label'),
                filterFn: 'contains',
            },
        ],
        []
    );


    const table = useMaterialReactTable<ICategory>({
        columns,
        defaultColumn: {
            maxSize: 400,
            minSize: 80,
            size: 80,
        },
        data: rows ? rows : [],
        columnFilterDisplayMode: 'popover',
        globalFilterFn: 'contains',
        enableFullScreenToggle: false,
        enableFilters: true,
        enableDensityToggle: true,
        muiTableHeadCellProps: getMuiTableHeadCellProps(theme.palette.primary.main),
        muiTableBodyCellProps: getMuiTableBodyCellProps(theme),
        muiTablePaperProps: getMuiTablePaperProps,
        enablePagination: rows ? rows?.length > 25 : false,
        initialState: {
            density: 'compact',
            pagination: {pageSize: 25, pageIndex: 0},
        },
        localization: MRT_Localization_IT,

        mrtTheme: (theme) => ({
            baseBackgroundColor: theme.palette.background.default,

        }),
        muiTableBodyRowProps: getMuiTableBodyRowProps(theme),
        renderTopToolbarCustomActions: ({table}) => (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <>
                    {/*<Avatar sx={{*/}
                    {/*    backgroundColor: 'black',*/}
                    {/*    border: `2px solid ${theme.palette.primary.main}`,*/}
                    {/*    marginRight: 1*/}
                    {/*}}>*/}
                    {/*    <img src={Images.navbar_listino_yellow} alt={'categories'}*/}
                    {/*         style={{width: '24px', height: '24px'}}/>*/}
                    {/*</Avatar>*/}
                    <MuiReadOnlyBoxBody label={''} value={'categories'}/>
                    <MRTMuiButtonWithAvatar
                        icon={<Add style={{color: theme.palette.primary.main, fontSize: '2rem'}}/>}
                        onClick={() => {
                            setSelectedRow(undefined);
                            setIsEditFormOpen(true);
                        }} label={t('add category')}/>

                </>
            </Box>
        ),

        enableRowActions: true,
        renderRowActionMenuItems: ({closeMenu, staticRowIndex, row}) => [
            onSelect && onClose && (
                <MRT_ActionMenuItem
                    icon={<AdsClickIcon color="primary"/>}
                    key="select"
                    label={t("select")}
                    onClick={() => {
                        onSelect(row.original);
                        onClose();
                    }}
                    table={table}
                />),
            <MRT_ActionMenuItem
                icon={<Edit color="primary"/>}
                key="edit"
                label={t("edit")}
                onClick={(event) => {
                    setSelectedRow(row.original);
                    setIsEditFormOpen(true)
                    closeMenu()
                }}
                table={table}
            />,
            <MRT_ActionMenuItem
                icon={<Delete color="warning"/>}
                key="delete"
                label={t("delete")}
                onClick={(event) => {
                    setSelectedRow(row.original);
                    setDeleteConfirmationOpen(true);
                    closeMenu()
                }}
                table={table}
            />,
        ],
    });


    return <>

        {onClose &&
            <Grid item xs={12} style={{textAlign: 'center'}}>
                <Box className={'mt-2 gap-x-2 mb-2'}>
                    <UndoButton onClose={onClose}/>
                </Box>
            </Grid>}


        <MaterialReactTable table={table}/>

        <MuiModal
            top="50%"
            left="50%"
            width={'100vH'}
            open={isEditFormOpen}
            onClose={() => setIsEditFormOpen(false)}
            disableBackdropClick={true}
        >
            <CategoryForm
                objToEdit={selectedRow}
                onClose={() => setIsEditFormOpen(false)}
            />
        </MuiModal>

        <MuiAlertDialog
            open={isDeleteConfirmationOpen}
            title="Delete Confirmation"
            content="Are you sure you want to delete this category?"
            onClose={handleCancelDelete}
            onConfirm={handleConfirmDelete}
        />

        <MuiModal
            top="50%"
            left="50%"
            width={'100vH'}
            open={isProcessingTableOpen}
            onClose={() => setIsProcessingTableOpen(false)}
            disableBackdropClick={true}
        >
            <ProcessingTable onClose={() => setIsProcessingTableOpen(false)}/>
        </MuiModal>
    </>;

};

export default CategoryTable;
